import { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { AgentChatContext, ChatMode } from '~/components/agent/AgentChatContext'
import {
  DEFAULT_PROMPT_TYPE,
  PROMPT_TEMPLATES,
  type PromptContext,
} from '~/components/agent/ingress/promptConfig'
import type {
  AgentIngressOptions,
  AgentIngressPromptType,
  IngressContextKey,
} from '~/components/agent/ingress/types'
import { useIsAgentChatEnabled } from '~/hooks/useIsAgentChatEnabled'
import { getHasOpenDialogs } from '~/store/duck/dialogs'
import { getPatient } from '~/store/reducers/patients'
import { InputMethod } from '~/utils/trackingProvider'

/**
 * Error thrown when required context is missing for a prompt type.
 * Consumers can catch this error to handle missing context gracefully.
 */
export class IngressContextError extends Error {
  constructor(
    public promptType: AgentIngressPromptType,
    public missingContext: IngressContextKey[],
  ) {
    super(
      `Missing required context for ${promptType}: ${missingContext.join(', ')}`,
    )
    this.name = 'IngressContextError'
  }
}

export interface UseAgentIngressReturn {
  handleIngress: (promptType?: AgentIngressPromptType) => Promise<void>
}

/**
 * Hook that provides a callback function for agent ingress buttons.
 * The callback opens the AI Assistant, creates a new session, and sends an initial message.
 *
 * @param options - Configuration including optional patientId and clientId
 * @returns Object with `handleIngress` callback function
 *
 * @example
 * ```tsx
 * const { handleIngress } = useAgentIngress({ patientId: '123' })
 *
 * <button onClick={() => handleIngress(AgentIngressPromptType.MEDICAL_HISTORY)}>
 *   Summarize Medical History
 * </button>
 *
 * ```
 */
export const useAgentIngress = (
  options: AgentIngressOptions,
): UseAgentIngressReturn => {
  const { patientId, clientId, buttonLocation } = options
  const location = useLocation()
  const isAgentChatEnabled = useIsAgentChatEnabled()
  const isModalOpen = useSelector(getHasOpenDialogs)
  const patient = useSelector(getPatient(patientId || ''))

  const agentChatContext = useContext(AgentChatContext)
  const hasAgentChatProvider = agentChatContext !== undefined

  const openChat = agentChatContext?.openChat ?? (() => {})
  const createNewSession =
    agentChatContext?.createNewSession ?? (async () => {})
  const sendMessage = agentChatContext?.sendMessage ?? (async () => {})
  const setChatMode = agentChatContext?.setChatMode ?? (() => {})

  const validateContext = (
    promptType: AgentIngressPromptType,
  ): PromptContext => {
    const template = PROMPT_TEMPLATES[promptType]
    if (!template) {
      throw new Error(`Unknown prompt type: ${promptType}`)
    }

    const context: PromptContext = {
      patientName: patient?.name,
      patientId,
      clientId,
    }

    const missingContext = template.requiredContext.filter(
      (requiredKey) => !context[requiredKey],
    )

    if (missingContext.length > 0) {
      throw new IngressContextError(promptType, missingContext)
    }

    if (template.validateContext && !template.validateContext(context)) {
      throw new IngressContextError(promptType, template.requiredContext)
    }

    return context
  }

  const buildPrompt = (promptType: AgentIngressPromptType) => {
    const template = PROMPT_TEMPLATES[promptType]
    if (!template) {
      const defaultTemplate = PROMPT_TEMPLATES[DEFAULT_PROMPT_TYPE]
      const context = validateContext(DEFAULT_PROMPT_TYPE)
      return defaultTemplate.buildPrompt(context)
    }

    const context = validateContext(promptType)
    return template.buildPrompt(context)
  }

  const executeIngress = useCallback(
    async (promptType: AgentIngressPromptType) => {
      if (!isAgentChatEnabled) {
        // eslint-disable-next-line no-console
        console.warn('useAgentIngress: Agent Chat is not enabled')
        return
      }

      // If AgentChatProvider is not available, silently return
      // This allows components to use useAgentIngress in tests without wrapping in AgentChatProvider
      if (!hasAgentChatProvider) {
        // eslint-disable-next-line no-console
        console.warn(
          'useAgentIngress: AgentChatProvider is not available. Ingress functionality will be disabled.',
        )
        return
      }

      try {
        const context = validateContext(promptType)

        // If a modal is open, switch to RAIL mode before opening chat
        if (isModalOpen) {
          setChatMode(ChatMode.RAIL)
        }

        await createNewSession()

        openChat()

        const message = buildPrompt(promptType)

        await sendMessage({
          content: message,
          inputMethod: InputMethod.INGRESS,
          promptMetadata: {
            ingress_prompt_type: promptType,
            ingress_button_location: buttonLocation || location.pathname,
            ...(context.patientId && { patient_id: context.patientId }),
            ...(context.clientId && { client_id: context.clientId }),
          },
        })
      } catch (error) {
        if (error instanceof IngressContextError) {
          // eslint-disable-next-line no-console
          console.error(
            `useAgentIngress: ${error.message}. Required context: ${error.missingContext.join(', ')}`,
          )
        } else {
          // eslint-disable-next-line no-console
          console.error('useAgentIngress: Failed to execute ingress', error)
        }
        throw error
      }
    },
    [
      isAgentChatEnabled,
      hasAgentChatProvider,
      isModalOpen,
      patient?.name,
      patientId,
      clientId,
      buttonLocation,
      location.pathname,
      openChat,
      createNewSession,
      sendMessage,
      setChatMode,
    ],
  )

  const handleIngress = useCallback(
    async (promptType?: AgentIngressPromptType) => {
      try {
        await executeIngress(promptType || DEFAULT_PROMPT_TYPE)
      } catch (error) {
        // Errors are logged in executeIngress
      }
    },
    [executeIngress],
  )

  return { handleIngress }
}
